/**
 * In-memory access-token store. Replaces the previous localStorage-backed
 * implementation (security issue #157).
 *
 * Why memory-only:
 *   - localStorage is reachable from any same-origin JS context (XSS, third-
 *     party scripts, future supply-chain compromise) and outlives the tab.
 *   - The refresh token + sessionId now live in an HttpOnly cookie set by
 *     the API. JS can't read them; the browser sends them automatically on
 *     `credentials: 'include'` requests to the API.
 *   - The short-lived access token (15 min) lives only in memory. On tab
 *     close it's gone; on tab reopen the app proactively calls /auth/refresh
 *     and the cookie regenerates the access token. UX is identical to before.
 *
 * Concurrency note: a module-scoped variable is per-tab. Multiple tabs each
 * have their own access token but share the cookie, so each tab refreshes
 * independently. Tab A signing out (cookie cleared) makes tab B's next
 * refresh fail — the user is logged out everywhere.
 */
let accessTokenInMemory: string | null = null;

/**
 * Claims pulled off the access token when it was minted via the admin
 * impersonation flow. `null` for normal user sessions. The fields are
 * derived from the JWT payload on every read; the token store doesn't
 * persist this separately, so there's no chance of it going stale relative
 * to the token itself.
 */
export interface ImpersonationClaims {
  impersonatedBy: string;
  jti: string;
  exp: number;
  iat: number;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) {
      return null;
    }
    // JWT uses base64url, which atob() doesn't accept directly — swap the
    // URL-safe chars before decoding.
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export const tokenStore = {
  getAccessToken(): string | null {
    return accessTokenInMemory;
  },

  setAccessToken(accessToken: string): void {
    accessTokenInMemory = accessToken;
  },

  clearTokens(): void {
    accessTokenInMemory = null;
  },

  hasTokens(): boolean {
    return accessTokenInMemory !== null;
  },

  /**
   * If the current access token is an impersonation token (admin-minted),
   * returns the impersonation claims. Returns null for a normal user
   * session or when no token is set. Pure derivation — call any time.
   */
  getImpersonationClaims(): ImpersonationClaims | null {
    if (!accessTokenInMemory) {
      return null;
    }
    const payload = decodeJwtPayload(accessTokenInMemory);
    if (!payload) {
      return null;
    }
    if (typeof payload.impersonated_by !== 'string') {
      return null;
    }
    return {
      impersonatedBy: payload.impersonated_by,
      jti: typeof payload.jti === 'string' ? payload.jti : '',
      exp: typeof payload.exp === 'number' ? payload.exp : 0,
      iat: typeof payload.iat === 'number' ? payload.iat : 0,
    };
  },
};
